import axios, { AxiosError } from 'axios';
import { showAPIError, showNetworkError, showRateLimitError, showAuthError } from '../utils/notification';

// Remove /api from the end of the URL if it exists to avoid duplication
const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3000').replace(/\/api$/, '');
const MAX_RETRIES = 1;
const RETRY_DELAY = 1000; // 1 second base delay
const MAX_RETRY_DELAY = 60000; // 60 seconds maximum delay

const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000, // Increased timeout to 30 seconds
  headers: {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'If-None-Match': '',  // Prevent 304 responses
    'If-Modified-Since': '',  // Prevent 304 responses
    'Content-Type': 'application/json',
  }
});

// Request queue with improved concurrency control and duplicate prevention
class RequestQueue {
  private queue: Array<{
    key: string;
    request: () => Promise<any>;
    resolve: (value: any) => void;
    reject: (error: any) => void;
  }> = [];
  private processing = false;
  private concurrentLimit = 2;
  private activeRequests = 0;
  private requestMap = new Map<string, Promise<any>>();
  private inProgressRequests = new Set<string>();
  private lastRequestTimes = new Map<string, number>();
  private minRequestInterval = 1000; // Minimum time between identical requests (1 second)

  async add(key: string, request: () => Promise<any>): Promise<any> {
    const now = Date.now();
    const lastRequestTime = this.lastRequestTimes.get(key) || 0;

    // If this exact request is currently in progress or was made too recently, reject it
    if (this.inProgressRequests.has(key) || (now - lastRequestTime < this.minRequestInterval)) {
      return Promise.reject(new Error('Request already in progress or too frequent'));
    }

    // If there's a pending request with the same key, return the existing promise
    if (this.requestMap.has(key)) {
      return this.requestMap.get(key);
    }

    // Update the last request time
    this.lastRequestTimes.set(key, now);

    const promise = new Promise((resolve, reject) => {
      this.queue.push({ key, request, resolve, reject });
    });

    this.requestMap.set(key, promise);
    this.process();
    return promise;
  }

  private async process(): Promise<void> {
    if (this.processing) return;
    this.processing = true;

    while (this.queue.length > 0 && this.activeRequests < this.concurrentLimit) {
      const { key, request, resolve, reject } = this.queue.shift()!;
      this.activeRequests++;
      this.inProgressRequests.add(key);

      try {
        const result = await request();
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        this.activeRequests--;
        this.requestMap.delete(key);
        this.inProgressRequests.delete(key);
        
        // Clean up old request times after 5 minutes
        setTimeout(() => {
          this.lastRequestTimes.delete(key);
        }, 300000);
      }

      // Add a small delay between requests
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    this.processing = false;

    // If there are more items and we're under the limit, continue processing
    if (this.queue.length > 0 && this.activeRequests < this.concurrentLimit) {
      this.process();
    }
  }
}

const requestQueue = new RequestQueue();

// Exponential backoff with jitter
const getRetryDelay = (retryCount: number) => {
  const baseDelay = Math.min(
    MAX_RETRY_DELAY,
    RETRY_DELAY * Math.pow(2, retryCount)
  );
  // Add random jitter of up to 25%
  return baseDelay + (Math.random() * baseDelay * 0.25);
};

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Handle token expiration
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      showAuthError();
      return Promise.reject(error);
    }

    // Handle rate limiting
    if (error.response?.status === 429) {
      showRateLimitError();
      const retryCount = (originalRequest as any)._retryCount || 0;
      
      if (retryCount < MAX_RETRIES) {
        (originalRequest as any)._retryCount = retryCount + 1;
        const retryAfter = parseInt(error.response.headers['retry-after'] || '0') * 1000;
        const delay = retryAfter || getRetryDelay(retryCount);
        
        // Generate a unique key for the request
        const requestKey = `${originalRequest.method}-${originalRequest.url}-${JSON.stringify(originalRequest.data)}`;
        
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            requestQueue.add(requestKey, () => api(originalRequest))
              .then(resolve)
              .catch(reject);
          }, delay);
        });
      }
    }

    // Handle network errors
    if (!error.response) {
      showNetworkError();
    } else {
      showAPIError(error);
    }

    return Promise.reject(error);
  }
);

// API endpoints with improved duplicate prevention
export const jobs = {
  getAll: (page = 1, limit = 20) => api.get(`/jobs?page=${page}&limit=${limit}`),
  get: (id: string) => api.get(`/jobs/${id}`),
  create: (data: any) => {
    const key = `create-job-${JSON.stringify(data)}`;
    return requestQueue.add(key, () => api.post('/jobs', data));
  },
  update: (id: string, data: any) => {
    const key = `update-job-${id}-${JSON.stringify(data)}`;
    return requestQueue.add(key, () => api.put(`/jobs/${id}`, data));
  },
  delete: (id: string) => {
    const key = `delete-job-${id}`;
    return requestQueue.add(key, () => api.delete(`/jobs/${id}`));
  },
  toggle: (id: string) => {
    const key = `toggle-job-${id}`;
    return requestQueue.add(key, () => api.patch(`/jobs/${id}/toggle`));
  },
  getLogs: (id: string) => api.get(`/jobs/${id}/logs`),
  export: () => api.get('/jobs/export', { responseType: 'json' }),
  import: (jobsArray: any[]) => api.post('/jobs/import', jobsArray),
  getCounts: () => api.get('/jobs/counts'),
  bulkToggle: (jobIds: string[], action: 'enable' | 'disable') => {
    return api.patch('/jobs/bulk-toggle', { jobIds, action });
  },
};

export const auth = {
  login: (email: string, password: string) => 
    api.post('/auth/login', { email, password }),
  register: (email: string, password: string, planId?: string, duration?: 'month' | 'year') => 
    api.post('/auth/register', { email, password, planId, duration }),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email: string) => 
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) => 
    api.post('/auth/reset-password', { token, password }),
  verifyEmail: (token: string) => 
    api.post('/auth/verify-email', { token }),
};

export const logs = {
  getAll: (params?: any) => api.get('/logs', { params }),
  getByJobId: (jobId: string) => api.get(`/jobs/${jobId}/logs`),
  getJobsWithLogs: () => api.get('/logs/jobs-with-logs'),
};

export const premium = {
  upgrade: () => api.post('/premium/upgrade'),
  getStatus: () => api.get('/premium/status'),
};

export const dashboard = {
  getUserJobs: () => jobs.getAll(),
  getUserLogs: (params?: any) => logs.getAll(params),
};

export const ADMIN_ENDPOINTS = {
  USERS: '/admin/users',
  PLANS: '/admin/plans',
  PUBLIC_PLANS: '/admin/public/plans',
};

export const admin = {
  getUsers: (page = 1, limit = 20) => api.get(ADMIN_ENDPOINTS.USERS + `?page=${page}&limit=${limit}`),
  createUser: (data: any) => api.post(ADMIN_ENDPOINTS.USERS, data),
  updateUser: (id: string, data: any) => 
    api.patch(`${ADMIN_ENDPOINTS.USERS}/${id}`, data),
  deleteUser: (id: string) => api.delete(`${ADMIN_ENDPOINTS.USERS}/${id}`),
  getJobs: (page = 1, limit = 20) => api.get(`/admin/jobs?page=${page}&limit=${limit}`),
  getLogs: (page = 1, limit = 20) => api.get(`/admin/logs?page=${page}&limit=${limit}`),
  getStats: () => api.get('/admin/stats'),
  getWorkerStats: () => api.get('/admin/worker-stats'),
  getServerStatus: () => api.get('/admin/server-status'),
  getPlans: (page = 1, limit = 20) => api.get(ADMIN_ENDPOINTS.PLANS + `?page=${page}&limit=${limit}`),
  createPlan: (data: any) => api.post(ADMIN_ENDPOINTS.PLANS, data),
  updatePlan: (id: string, data: any) => 
    api.patch(`${ADMIN_ENDPOINTS.PLANS}/${id}`, data),
  deletePlan: (id: string) => api.delete(`${ADMIN_ENDPOINTS.PLANS}/${id}`),
  getPublicPlans: () => api.get(ADMIN_ENDPOINTS.PUBLIC_PLANS),
  getJobLogs: (jobId: string) => api.get(`/admin/jobs/${jobId}/logs`),
  runScheduler: () => {
    const key = 'run-scheduler';
    return requestQueue.add(key, () => api.post('/admin/run-scheduler'));
  },
  toggleJob: (jobId: string) => {
    const key = `admin-toggle-job-${jobId}`;
    return requestQueue.add(key, () => api.patch(`/admin/jobs/${jobId}/toggle`));
  },
  getOrders: (page = 1, limit = 20, filters = {}) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit), ...filters });
    return api.get(`/admin/orders?${params.toString()}`);
  },
  getOrder: (id: string) => api.get(`/admin/orders/${id}`),
};

// Add a new function for health check
export const getHealthStatus = async (): Promise<any> => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    console.error('Error fetching health status:', error);
    // Rethrow or handle as needed, maybe return a default error state
    throw error; 
  }
};

// Add a new function for user-facing health check
export const getUserHealthStatus = async (): Promise<any> => {
  try {
    const response = await api.get('/health/public');
    return response.data;
  } catch (error) {
    // If the health endpoint fails, treat as server error
    return { status: 'ERROR', message: 'Unable to reach server health endpoint.' };
  }
};

export const userSettings = {
  update: (data: any) => {
    const key = `update-settings-${JSON.stringify(data)}`;
    return requestQueue.add(key, () => api.patch('/auth/me', data));
  }
};

export const reseller = {
  getDashboard: () => api.get('/reseller/dashboard'),
  getOrders: (page = 1, pageSize = 10) => api.get(`/reseller/orders?page=${page}&limit=${pageSize}`),
  getLogs: (page = 1, pageSize = 10) => api.get(`/reseller/logs?page=${page}&limit=${pageSize}`),
  regenerateApiKey: () => api.post('/reseller/regenerate-api-key'),
};

export const payments = {
  getPayment: (paymentId: string) => api.get(`/payments/${paymentId}`),
};

export default api; 